import { Link } from 'react-router-dom';
import { MarketingPage, Section } from '@/modules/marketing/MarketingPage';
import { usePageMeta } from '@/lib/seo';
import { PAGES } from '@/seo/pages';

/**
 * Privacy Policy and Terms of Service.
 *
 * ## Written to be published, not to be reviewed later
 *
 * An earlier draft carried bracketed placeholders for the company name, address and governing
 * jurisdiction. A page with `[Company Address]` on it is not a policy; it is a note to self that
 * happens to be on the public internet. These name no country, no address and no company entity,
 * which is the only honest way to publish without inventing details.
 *
 * ## Plain language, accurate facts
 *
 * The previous versions read like engineering documentation because they were written from the
 * code. The facts survived that; the register did not. Everything stated here is still true of the
 * platform — no database passwords asked for, no backup contents retained, no secret readable back
 * through the API — but said the way a customer would need to hear it.
 *
 * ## Rewritten for the September 2026 change of product
 *
 * The platform stopped deploying applications and managing domains, DNS, certificates and databases.
 * Both documents describe what it does now: automation, monitoring with automatic fixes, and backups
 * with verification. The policy promises to tell account holders about significant changes rather
 * than quietly editing this page, and this is one.
 *
 * These are not legal advice. Have a lawyer read them before relying on them commercially.
 */

const CONTACT_LINK = (
  <Link className="text-primary hover:underline" to="/contact">
    contact page
  </Link>
);

export function PrivacyPage() {
  usePageMeta(PAGES.privacy);

  return (
    <MarketingPage
      eyebrow="Privacy Policy"
      title="Privacy Policy"
      standfirst="What we collect, what we deliberately do not, and what we do with the rest."
    >
      <p className="text-muted-foreground/70 mt-12 text-sm">Last updated: 23 September 2026</p>

      <Section title="1. Information We Collect">
        <p>
          <strong className="text-foreground">Account information.</strong> Your email address, a
          securely hashed password, and your two-factor settings if you enable them.
        </p>
        <p>
          <strong className="text-foreground">Infrastructure configuration.</strong> The servers you
          connect and what the platform detects about them, your monitors and where their alerts go,
          and your backup sources, destinations and schedules, together with the credentials needed
          to operate them, which are encrypted.
        </p>
        <p>
          <strong className="text-foreground">Automation, monitoring and backup records.</strong>{' '}
          The runs you start (the playbooks, templates and commands, with what each server printed),
          server resource readings, monitor check results, incidents and the fixes attempted on them,
          and backup and verification records.
        </p>
        <p>
          <strong className="text-foreground">Billing information.</strong> Your subscription and
          invoices.
        </p>
      </Section>

      <Section title="2. Information We Do Not Collect">
        <p>
          <strong className="text-foreground">Database passwords.</strong> Backups read your
          databases with the server’s own administrator access, so we never ask for a database
          password.
        </p>
        <p>
          <strong className="text-foreground">Backup contents.</strong> Backups upload directly from
          your server to storage you own, and restore tests run on your server. When you download a
          copy that is kept on your server, it is streamed through the platform to you and not
          stored.
        </p>
        <p>
          <strong className="text-foreground">Payment card details.</strong> These are handled
          entirely by our payment provider and never reach our servers.
        </p>
        <p>
          <strong className="text-foreground">Conversations with Juni.</strong> Questions put to our
          assistant, and its answers, are not stored and not used to train anything. See below.
        </p>
      </Section>

      <Section title="3. Juni, Our Assistant">
        <p>
          Juni answers questions on this website and in the dashboard. It runs on our own servers
          from an open-source model: your question is not sent to any other company, and no
          third-party AI service is involved.
        </p>
        <p>
          Nothing is kept. A question and its answer are not written to a database or to a
          transcript. The conversation stays in your browser tab and is gone when you close it; the
          last few exchanges travel with each question only so a follow-up makes sense.
        </p>
        <p>
          When you are signed in, Juni may describe your own account (your servers, monitors,
          backups, runs and subscription) to answer you. It never describes anybody else’s account,
          and on this public website it cannot see an account at all. It can read; it cannot change
          anything, start a run or reach your servers.
        </p>
      </Section>

      <Section title="4. How We Use Information">
        <p>
          To run the service: carrying out the playbooks, templates and commands you start, running
          your monitors and the automatic fixes you allow, and taking and verifying your scheduled
          backups.
        </p>
        <p>
          To support you: answering your questions, and diagnosing failures using the run, monitoring
          and backup records attached to your account.
        </p>
        <p>To bill you, and to send necessary account messages such as password resets.</p>
        <p>
          To keep the platform secure and to meet our legal obligations. We do not sell personal
          data, and we do not share it for advertising.
        </p>
      </Section>

      <Section title="5. Third-Party Services">
        <p>
          A small number of providers help us run the service: hosting for the platform itself, a
          payment provider for subscriptions, and an email provider for account messages. Each
          receives only what it needs to do its job.
        </p>
        <p>
          Your own hosting provider is not one of ours. You chose them and you pay them directly;
          we are not a party to that relationship.
        </p>
      </Section>

      <Section title="6. Data Retention">
        <p>
          Account and infrastructure information, run history, incidents and backup records are kept
          for as long as your account is open. Monitor check results and server resource readings
          are kept for thirty days.
        </p>
        <p>
          When you close your account we delete your data, except where we are required to keep
          records such as invoices, which also means a refund request can still be verified after
          an account has closed. Nothing on your servers is removed, and backups in your own storage
          stay there: closing your account removes the platform’s access, not your data.
        </p>
      </Section>

      <Section title="7. Data Security">
        <p>
          Server passwords and keys, sudo passwords and storage keys are encrypted. Once saved, none
          of them can be read back through the dashboard or the API. You can replace one, but
          nobody can retrieve it.
        </p>
        <p>
          A sudo password is given to sudo on its standard input rather than on a command line, and
          each server’s identity is verified on every connection. Commands you run are kept in your
          run history exactly as you typed them, so do not put passwords or tokens in them. Your
          backups we have deliberately arranged not to hold at all.
        </p>
      </Section>

      <Section title="8. Your Choices">
        <p>
          You can view and update most of your information directly in your dashboard, and you can
          close your account at any time.
        </p>
        <p>
          You can also ask us for a copy of the information we hold about you, ask us to correct it,
          or ask us to delete it. Depending on where you live, you may have additional rights under
          local data protection law.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          We may update this policy as the service changes. If a change is significant, we will tell
          account holders rather than quietly updating this page.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>Questions about this policy, or about your data, can be sent through our {CONTACT_LINK}.</p>
      </Section>
    </MarketingPage>
  );
}

export function TermsPage() {
  usePageMeta(PAGES.terms);

  return (
    <MarketingPage
      eyebrow="Terms of Service"
      title="Terms of Service"
      standfirst="The agreement between you and Techie Gain, in plain language."
    >
      <p className="text-muted-foreground/70 mt-12 text-sm">Last updated: 22 September 2026</p>

      <Section title="1. Overview">
        <p>
          Techie Gain is an infrastructure operations platform. It connects to servers you already
          run, carries out the playbooks, templates and commands you start on them, monitors them,
          attempts limited automatic fixes where you allow it, and backs up and verifies the data you
          choose.
        </p>
        <p>
          We are not a hosting provider. We do not sell or resell servers, and we are not a party to
          the agreement between you and whoever you rent your servers from.
        </p>
        <p>By creating an account, you agree to these terms.</p>
      </Section>

      <Section title="2. Accounts">
        <p>
          You are responsible for your account and for everything done through it. Keep your
          credentials secure, and enable two-factor authentication.
        </p>
        <p>
          An account belongs to one person. Sharing an account means sharing credentials, which we
          do not recommend.
        </p>
        <p>You must be able to enter into a binding agreement to use the service.</p>
      </Section>

      <Section title="3. Your Infrastructure">
        <p>
          Your servers are yours. You own them, you pay for them, and they remain under your
          control at all times.
        </p>
        <p>
          You confirm you are entitled to connect and modify every server you add. The platform will
          install and update software, change configuration, restart services and run the commands you
          give it on those machines. That is what it is for.
        </p>
        <p>
          With automatic fixes switched on for a monitor, the platform may restart stopped services,
          reload services, and clear package caches and old system journal entries without asking
          first. It will not reboot a server, delete your data, change firewall rules or roll back
          software on its own.
        </p>
        <p>
          You remain responsible for your infrastructure, including paying for it, securing it and
          keeping it maintained beyond the work the platform performs, and for the commands and changes
          you start through the platform.
        </p>
      </Section>

      <Section title="4. Acceptable Use">
        <p>
          Do not use the platform to run or operate anything unlawful, to send unsolicited bulk
          email, to host malware, to attack or scan systems you do not control, or to infringe
          anyone’s rights.
        </p>
        <p>
          Monitors may only target systems you own or have permission to check. A monitor makes
          repeated automated requests, and pointing one at somebody else’s service without their
          agreement is not permitted.
        </p>
      </Section>

      <Section title="5. Billing and Refunds">
        <p>
          <strong className="text-foreground">Free trial.</strong> A new account starts with one free
          trial, with the length and limits shown on the pricing page. No payment details are needed
          and nothing is charged for it. Each customer receives one free trial. It ends when its term
          runs out or when a paid plan starts, whichever comes first. When it runs out, adding and
          changing infrastructure, monitor checks and scheduled backups stop until you choose a plan;
          nothing you set up is deleted.
        </p>
        <p>
          Paid plans are billed in advance for the period you select.
        </p>
        <p>
          <strong className="text-foreground">7-day money-back guarantee.</strong> Techie Gain
          offers a 7-day money-back guarantee for first-time customers. If the platform is not a
          good fit for your workflow, contact support within 7 days of your initial subscription
          purchase to request a full refund.
        </p>
        <p>
          This guarantee applies only to the first payment for a subscription and does not apply to
          renewal charges. You can cancel at any time to prevent future billing, though cancelling
          does not by itself refund a period you have already paid for. Refund requests are made
          through support.
        </p>
        <p>
          Fraudulent, abusive, or intentionally harmful use of the platform may void refund
          eligibility.
        </p>
        <p>
          If a renewal payment fails we retry it and let you know. If it is still unpaid 7 days after
          the renewal date, the subscription is cancelled and nothing more is charged. Cancellation
          stops automation, monitoring and scheduled backups; it does not stop anything running on
          your servers.
        </p>
        <p>When you cancel, your plan continues until the end of the period you have paid for.</p>
      </Section>

      <Section title="6. Availability">
        <p>
          We work to keep the platform available, but we do not guarantee uninterrupted service and
          we make no uptime commitment under these terms.
        </p>
        <p>
          Importantly, your servers do not depend on us being online. Nothing the platform installs
          needs it to keep running. If the platform is unavailable, your servers keep running. For
          that time you lose automation, monitoring and scheduled backups, not your software.
        </p>
      </Section>

      <Section title="7. Limitation of Liability">
        <p>The service is provided on an “as is” basis.</p>
        <p>
          To the fullest extent permitted by law, we are not liable for indirect or consequential
          losses, lost profits, or lost data. Our total liability is limited to the amount you paid
          us in the twelve months before the claim arose.
        </p>
        <p>
          Keep your own backups. The platform can schedule them to storage you control and test that
          they restore, but because we hold no copy of their contents, we cannot restore data on your
          behalf.
        </p>
      </Section>

      <Section title="8. Termination">
        <p>
          You can close your account at any time. We may suspend or close an account for non-payment
          or for a breach of these terms, with notice where circumstances allow.
        </p>
        <p>
          When an account closes we remove its data from the platform. Everything on your servers
          stays where it is and keeps running. We recommend rotating any credentials you gave us
          once you leave.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          We may update these terms as the service develops. Significant changes will be notified to
          account holders rather than made quietly, and continued use after a change means you
          accept it.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>Questions about these terms can be sent through our {CONTACT_LINK}.</p>
      </Section>
    </MarketingPage>
  );
}
